<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useFlowieStore } from '../../../stores/flowieStore.js';

const store = useFlowieStore();
const currentPage = ref(1);
const projectsPerPage = 5;

const totalProjectPages = computed(() => {
    return Math.max(
        1,
        Math.ceil(store.conversations.projects.length / projectsPerPage)
    );
});

const paginatedProjects = computed(() => {
    const startIndex = (currentPage.value - 1) * projectsPerPage;
    const endIndex = startIndex + projectsPerPage;

    return store.conversations.projects.slice(startIndex, endIndex);
});

onMounted(() => {
    store.loadConversations().then(() => store.restoreConversationOnOpen());
});

watch(
    () => store.conversations.projects.length,
    () => {
        if (currentPage.value > totalProjectPages.value) {
            currentPage.value = totalProjectPages.value;
        }
    }
);

const openConversation = (id) => {
    store.loadConversation(id);
    // Scroll to chat on mobile
    const chatEl = document.querySelector('[data-flowie-chat]');
    if (chatEl && window.innerWidth < 768) {
        chatEl.scrollIntoView({ behavior: 'smooth' });
    }
};

const hideProject = (id) => {
    store.hideConversation(id);
};

const goToPreviousPage = () => {
    if (currentPage.value === 1) {
        return;
    }

    currentPage.value -= 1;
};

const goToNextPage = () => {
    if (currentPage.value === totalProjectPages.value) {
        return;
    }

    currentPage.value += 1;
};
</script>

<template>
    <div>
        <!-- Projects -->
        <div
            v-if="store.conversations.projects.length"
            class="space-y-2 mb-4"
        >
            <list-basic>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h2 class="text-base font-bold text-gray-800">
                            {{ _mft('dashboard:flowie.projects.title') }}
                        </h2>
                    </div>
                </template>

                <list-basic-item
                    v-for="project in paginatedProjects"
                    :key="project.id"
                    style-type="default"
                    size="lg"
                    class="w-full !justify-start !text-left items-start gap-2 cursor-pointer group"
                >
                    <div class="flex items-start gap-3 w-full relative group">
                        <div
                            class="min-w-0 flex-1"
                            @click="openConversation(project.id)"
                        >
                            <span
                                class="block text-xs text-gray-800 truncate group-hover:text-gray-500"
                                :class="
                                    store.activeConversation?.id === project.id
                                        ? 'font-bold'
                                        : 'font-medium'
                                "
                                >{{
                                    project.title ||
                                    _mft('dashboard:flowie.projects.untitled')
                                }}</span
                            >
                            <span v-if="project.progress" class="block mt-1">
                                <span
                                    class="flex items-center justify-between mb-1"
                                >
                                    <span class="text-xs text-gray-400">
                                        {{ project.progress.completed }}
                                        {{
                                            _mft(
                                                'dashboard:flowie.actionPlan.progress'
                                            )
                                        }}
                                        {{ project.progress.total }}
                                        {{
                                            _mft(
                                                'dashboard:flowie.projects.tasksCompleted'
                                            ).toLowerCase()
                                        }}
                                    </span>
                                </span>
                                <span
                                    class="block w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"
                                >
                                    <span
                                        class="h-full transition-all duration-500 rounded-full bg-wlPrimary"
                                        :style="{
                                            width:
                                                (project.progress.total
                                                    ? (project.progress
                                                          .completed /
                                                          project.progress
                                                              .total) *
                                                      100
                                                    : 0) + '%',
                                        }"
                                    ></span>
                                </span>
                            </span>
                        </div>
                        <div
                            class="hidden group-hover:block absolute right-0 top-2"
                        >
                            <button-with-confirmation
                                size="sm"
                                :aria-label="_mft('shared:action.remove')"
                                :title="_mft('shared:action.remove')"
                                @confirmed="hideProject(project.id)"
                                :confirm-text="
                                    _mft('shared:confirm.areYouSure')
                                "
                            >
                                <i
                                    class="fa-regular fa-trash text-red-400/50"
                                ></i>
                            </button-with-confirmation>
                        </div>
                    </div>
                </list-basic-item>

                <template v-if="totalProjectPages > 1" #footer>
                    <div class="flex items-center justify-center gap-3 pt-2">
                        <button-base
                            size="sm"
                            :disabled="currentPage === 1"
                            :aria-label="'Previous page'"
                            :class="{
                                'pointer-events-none': currentPage === 1,
                            }"
                            @click="goToPreviousPage"
                        >
                            <span aria-hidden="true">&larr;</span>
                        </button-base>

                        <span
                            class="min-w-16 text-center text-xs text-gray-400"
                        >
                            {{ currentPage }} / {{ totalProjectPages }}
                        </span>

                        <button-base
                            size="sm"
                            :disabled="currentPage === totalProjectPages"
                            :aria-label="'Next page'"
                            :class="{
                                'pointer-events-none':
                                    currentPage === totalProjectPages,
                            }"
                            @click="goToNextPage"
                        >
                            <span aria-hidden="true">&rarr;</span>
                        </button-base>
                    </div>
                </template>
            </list-basic>
        </div>
    </div>
</template>
